import React, { useState, useEffect } from 'react'

import {
    Chart,
    ChartCategoryAxis,
    ChartCategoryAxisItem,
    ChartLegend,
    ChartLegendItem,
    ChartTitle,
    ChartSeries,
    ChartSeriesItem,
    ChartSeriesLabels,
    ChartValueAxis,
    ChartValueAxisItem,
    ChartTooltip,
} from '@progress/kendo-react-charts'
import { geometry, drawing } from '@progress/kendo-drawing'
import { TextField } from '@material-ui/core'
import { MoneyInput } from '../../../GlobalComponents/Inputs/Inputs'

import styles from '../../TechPartnerDashboard/TechPartnerDashboard.module.scss'

import {
    fetchSalesOpportunity,
    SalesOpportunityData,
    // fetchSalesProcessSales,
    SalesProcessSalesData,
    SalesProcessSalesLabelData,
    fetchTechPartnerBookings,
    YTDData,
    fetchTechPartnerPlan,
    fetchTechPartnerSales,
    fetchTechPartnerBacklog,
    fetchTechPartnerCOGS,
    MonthlySalesData,
    TechPartnerSalesResults,
    CostData,
} from '../../../features/sales/salesAPI'
import { TechPartnerDashboardDataSectionProps } from '../TechPartnerDashboardSection'
import { HoverIcon } from '../../../GlobalComponents/_icons/HoverIcon'

export const SalesProcessData = (props: TechPartnerDashboardDataSectionProps) => {
    const [opportunityDataPayload, setOpportunityDataPayload] = useState<
        Array<SalesOpportunityData>
    >([])
    const [ytdTotal, setYTDTotal] = useState<number>(0)
    const [pytdTotal, setPYTDTotal] = useState<number>(0)
    const [deltaTotal, setDeltaTotal] = useState<number>(0)
    const [deltaPercentTotal, setDeltaPercentTotal] = useState<number>(0)
    const [percentOfStagesTotal, setPercentOfStagesTotal] = useState<number>(0)

    const [openLeads, setOpenLeads] = useState<number>(0)
    const [contactingLeads, setContactingLeads] = useState<number>(0)
    const [waitingForInfoLeads, setWaitingForInfoLeads] = useState<number>(0)
    const [quotingLeads, setQuotingLeads] = useState<number>(0)
    const [waitingForPOLeads, setWaitingForPOLeads] = useState<number>(0)
    const [leadsTotal, setLeadsTotal] = useState<number>(0)

    const [refreshChart, setRefreshChart] = useState<boolean>(true)

    const [salesProcessSalesDataPayload, setSalesProcessSalesDataPayload] = useState<
        Array<SalesProcessSalesData>
    >([])
    const [bookingsDataPayload, setBookingsDataPayload] = useState<YTDData>()
    const [planDataPayload, setPlanDataPayload] = useState<Array<MonthlySalesData>>()
    const [salesDataPayload, setSalesDataPayload] = useState<TechPartnerSalesResults>()
    const [backlogDataPayload, setBacklogDataPayload] = useState<number | null>(null)
    // const [cogsDataPayload, setCOGSDataPayload] = useState<Array<CostData>>()

    const [bookingsQuotaYoY, setBookingsQuotaYoY] = useState<number>(0)
    const [salesQuotaYoY, setSalesQuotaYoY] = useState<number>(0)

    useEffect(() => {
        if (Object.keys(props.selectedInputs).length === 0) return

        props.onLoadingChange(true)
        setBacklogDataPayload(null)

        let dataSalesOpportunity: Array<SalesOpportunityData>

        // let dataSalesProcessSales: Array<SalesProcessSalesData>
        let dataBookings: YTDData
        let dataPlan: Array<MonthlySalesData>
        let dataSales: TechPartnerSalesResults
        let dataBacklog: number
        // let dataBacklog: YTDData
        // let dataCOGS: Array<CostData>

        async function getSalesOpportunity() {
            if (Object.keys(props.selectedInputs).length === 0) return

            dataSalesOpportunity = await fetchSalesOpportunity(props.selectedInputs)
        }

        // async function getSalesProcessSales() {
        //     if (Object.keys(props.selectedInputs).length === 0) return

        //     dataSalesProcessSales = await fetchSalesProcessSales(props.selectedInputs)
        // }

        async function getTechPartnerBookings() {
            if (Object.keys(props.selectedInputs).length === 0) return

            dataBookings = await fetchTechPartnerBookings(props.selectedInputs)
        }

        async function getTechPartnerPlan() {
            if (Object.keys(props.selectedInputs).length === 0) return

            dataPlan = await fetchTechPartnerPlan(props.selectedInputs)
        }

        async function getTechPartnerSales() {
            if (Object.keys(props.selectedInputs).length === 0) return

            dataSales = await fetchTechPartnerSales(props.selectedInputs)
        }

        async function getTechPartnerBacklog() {
            if (Object.keys(props.selectedInputs).length === 0) return

            dataBacklog = await fetchTechPartnerBacklog(props.selectedInputs)
        }

        // async function getTechPartnerCOGS() {
        //     if (Object.keys(props.selectedInputs).length === 0) return

        //     dataCOGS = await fetchTechPartnerCOGS(props.selectedInputs)
        // }

        // run get functions in parallel, but for both to finish to avoid extra renders
        const asyncFunctions = [
            getSalesOpportunity,
            getTechPartnerBookings,
            getTechPartnerPlan,
            getTechPartnerSales,
            // getTechPartnerBacklog,
            // getTechPartnerCOGS,
        ]
        const getData = async () => {
            await Promise.all(
                asyncFunctions.map(async (get) => {
                    await get()
                })
            )
        }
        getTechPartnerBacklog().then(() => {
            if (dataBacklog) setBacklogDataPayload(dataBacklog)
        })

        getData().then(() => {
            if (dataSalesOpportunity) {
                setOpportunityDataPayload(dataSalesOpportunity)
                setRefreshChart(true)
            }

            if (dataBookings) setBookingsDataPayload(dataBookings)
            if (dataPlan) setPlanDataPayload(dataPlan)
            if (dataSales) setSalesDataPayload(dataSales)
            // if (dataBacklog) setBacklogDataPayload(dataBacklog)
            // if (dataCOGS) setCOGSDataPayload(dataCOGS)
            // initTechPartnerBacklog() // #12213 match tech dasbh backlog to snb dashboard
            // if (dataSalesProcessSales)
            //     setSalesProcessSalesDataPayload(
            //         dataSalesProcessSales.sort((a, b) => a.SortOrder - b.SortOrder)
            //     )
        })
    }, [props.selectedInputs])

    useEffect(() => {
        if (Object.keys(props.selectedInputs).length > 0) calculateSalesOpportunities()

        hideLoadingVeilIfAllCalculationsAreComplete()
    }, [opportunityDataPayload])

    const calculateSalesOpportunities = () => {
        let dYTDTotal = opportunityDataPayload?.reduce((a, b) => a + b.YTD, 0)
        if (!dYTDTotal) dYTDTotal = 0
        setYTDTotal(dYTDTotal)

        let dPYTDTotal = opportunityDataPayload?.reduce((a, b) => a + b.PYTD, 0)
        if (!dPYTDTotal) dPYTDTotal = 0
        setPYTDTotal(dPYTDTotal)

        let dDeltaTotal = opportunityDataPayload?.reduce((a, b) => a + b.Delta, 0)
        if (!dDeltaTotal) dDeltaTotal = 0
        setDeltaTotal(dDeltaTotal)

        if (dPYTDTotal > 0) {
            let dDeltaPercentTotal = dYTDTotal / dPYTDTotal
            if (!dDeltaPercentTotal) dDeltaPercentTotal = 0
            setDeltaPercentTotal(dDeltaPercentTotal)
        }

        let dPercentOfStagesTotal = opportunityDataPayload?.reduce(
            (a, b) => a + b.PercentOfStages,
            0
        )
        if (!dPercentOfStagesTotal) dPercentOfStagesTotal = 0
        setPercentOfStagesTotal(dPercentOfStagesTotal)
    }

    const salesProcessSalesLabels: SalesProcessSalesLabelData[] = [
        {
            Label: 'Bookings',
            SecondaryLabel: 'Orders received YTD',
            SortOrder: 10,
        },
        {
            Label: 'Sales',
            SecondaryLabel: 'Orders invoiced',
            SortOrder: 20,
        },
        {
            Label: 'Backlog',
            SecondaryLabel: 'Open orders',
            SortOrder: 30,
        },
        {
            Label: 'COGS',
            SecondaryLabel: 'Cost of Goods Sold',
            SortOrder: 40,
        },
    ]

    useEffect(() => {
        if (Object.keys(props.selectedInputs).length > 0) calculateBookings()

        hideLoadingVeilIfAllCalculationsAreComplete()
    }, [bookingsDataPayload, bookingsQuotaYoY])

    const calculateBookings = () => {
        if (!bookingsDataPayload) return

        let dDelta = bookingsDataPayload.YTD - bookingsDataPayload.PYTD
        let dDeltaPercent = 0
        if (bookingsDataPayload.PYTD !== 0)
            dDeltaPercent = bookingsDataPayload.YTD / bookingsDataPayload.PYTD

        let dGap = bookingsDataPayload.YTD - bookingsQuotaYoY
        let dGapPercent = 0
        if (bookingsQuotaYoY !== 0) dGapPercent = bookingsDataPayload.YTD / bookingsQuotaYoY

        let bookingsLabelData = salesProcessSalesLabels.find((l) => l.Label === 'Bookings')

        setSalesProcessSalesDataPayload((curArray) => [
            ...curArray.filter((c) => c.Label !== bookingsLabelData?.Label),
            {
                Label: bookingsLabelData ? bookingsLabelData.Label : '',
                SecondaryLabel: bookingsLabelData ? bookingsLabelData.SecondaryLabel : '',
                SortOrder: bookingsLabelData ? bookingsLabelData.SortOrder : 0,
                YTD: bookingsDataPayload.YTD,
                PYTD: bookingsDataPayload.PYTD,
                Delta: dDelta,
                DeltaPercent: dDeltaPercent,
                Plan: undefined,
                QuotaYoY: bookingsQuotaYoY,
                Gap: dGap,
                GapPercent: dGapPercent,
            },
        ])
    }

    useEffect(() => {
        if (Object.keys(props.selectedInputs).length > 0) calculateSales()

        hideLoadingVeilIfAllCalculationsAreComplete()
    }, [salesDataPayload, planDataPayload, salesQuotaYoY])

    const calculateSales = () => {
        if (!(salesDataPayload && planDataPayload)) return

        calculateCOGS()

        let CurrentMonth = new Date().getMonth()
        const today = new Date()
        const daysInMonth = (year: number, month: number) => {
            return new Date(year, month, 0).getDate()
        }
        let fractionOfTheMonth =
            (today.getDate() - 1) / daysInMonth(today.getFullYear(), today.getMonth() + 1)

        let dSalesYTD = salesDataPayload.CalendarYearSalesYTD
        if (!dSalesYTD) dSalesYTD = 0

        let dPlanYTD = planDataPayload
            .filter((month) => month.MonthAge >= -CurrentMonth && month.MonthAge <= 0)
            .reduce((a, b) => a + (b.MonthAge !== 0 ? b.Sales : b.Sales * fractionOfTheMonth), 0)
        if (!dPlanYTD) dPlanYTD = 0

        let dSalesPYTD = salesDataPayload.CalendarYearSalesPYTD
        if (!dSalesPYTD) dSalesPYTD = 0

        let dDelta = dSalesYTD - dSalesPYTD
        let dDeltaPercent = 0
        if (dSalesPYTD !== 0) dDeltaPercent = dSalesYTD / dSalesPYTD

        let dGap = dSalesYTD - salesQuotaYoY
        let dGapPercent = 0
        if (salesQuotaYoY !== 0) dGapPercent = dSalesYTD / salesQuotaYoY

        let salesLabelData = salesProcessSalesLabels.find((l) => l.Label === 'Sales')

        setSalesProcessSalesDataPayload((curArray) => [
            ...curArray.filter((c) => c.Label !== salesLabelData?.Label),
            {
                Label: salesLabelData ? salesLabelData.Label : '',
                SecondaryLabel: salesLabelData ? salesLabelData.SecondaryLabel : '',
                SortOrder: salesLabelData ? salesLabelData.SortOrder : 0,
                YTD: dSalesYTD,
                PYTD: dSalesPYTD,
                Delta: dDelta,
                DeltaPercent: dDeltaPercent,
                Plan: dPlanYTD,
                QuotaYoY: salesQuotaYoY,
                Gap: dGap,
                GapPercent: dGapPercent,
            },
        ])
    }

    useEffect(() => {
        if (Object.keys(props.selectedInputs).length > 0) calculateBacklog()

        // hideLoadingVeilIfAllCalculationsAreComplete()
        // }, [salesProcessSalesDataPayload])
    }, [backlogDataPayload])

    const initTechPartnerBacklog = () => {
        if (backlogDataPayload) return

        let backlogLabelData = salesProcessSalesLabels.find((l) => l.Label === 'Backlog')

        setSalesProcessSalesDataPayload((curArray) => [
            ...curArray.filter((c) => c.Label !== backlogLabelData?.Label),
            {
                Label: backlogLabelData ? backlogLabelData.Label : '',
                SecondaryLabel: backlogLabelData ? backlogLabelData.SecondaryLabel : '',
                SortOrder: backlogLabelData ? backlogLabelData.SortOrder : 0,
                YTD: null,
                PYTD: undefined,
                Delta: undefined,
                DeltaPercent: undefined,
                Plan: undefined,
                QuotaYoY: undefined,
                Gap: undefined,
                GapPercent: undefined,
            },
        ])
    }

    const calculateBacklog = () => {
        if (!backlogDataPayload) return

        // let bookingsRow = salesProcessSalesDataPayload.find((c) => c.Label === 'Bookings')
        // let salesRow = salesProcessSalesDataPayload.find((c) => c.Label === 'Sales')
        // let backlogRow = salesProcessSalesDataPayload.find((c) => c.Label === 'Backlog')
        // if (!bookingsRow || !salesRow || backlogRow) return

        // let backlogYTD = bookingsRow.YTD - salesRow.YTD
        // let backlogPYTD = bookingsRow.PYTD - salesRow.PYTD

        // let dDelta = backlogYTD - backlogPYTD
        // let dDeltaPercent = 0
        // if (backlogPYTD !== 0) dDeltaPercent = backlogYTD / backlogPYTD
        // let dDelta = backlogDataPayload.YTD - backlogDataPayload.PYTD
        // let dDeltaPercent = 0
        // if (backlogDataPayload.PYTD !== 0)
        //     dDeltaPercent = backlogDataPayload.YTD / backlogDataPayload.PYTD

        let backlogLabelData = salesProcessSalesLabels.find((l) => l.Label === 'Backlog')

        setSalesProcessSalesDataPayload((curArray) => [
            ...curArray.filter((c) => c.Label !== backlogLabelData?.Label),
            {
                Label: backlogLabelData ? backlogLabelData.Label : '',
                SecondaryLabel: backlogLabelData ? backlogLabelData.SecondaryLabel : '',
                SortOrder: backlogLabelData ? backlogLabelData.SortOrder : 0,
                // YTD: backlogYTD,
                // PYTD: backlogPYTD,
                YTD: backlogDataPayload,
                // YTD: backlogDataPayload.YTD,
                // PYTD: backlogDataPayload.PYTD,
                // Delta: dDelta,
                // DeltaPercent: dDeltaPercent,
                PYTD: undefined,
                Delta: undefined,
                DeltaPercent: undefined,
                Plan: undefined,
                QuotaYoY: undefined,
                Gap: undefined,
                GapPercent: undefined,
            },
        ])
    }

    const calculateCOGS = () => {
        if (!salesDataPayload) return // #10750 moved cogs into salesDataPayload 6/26/2024

        const today = new Date()
        const daysInMonth = (year: number, month: number) => {
            return new Date(year, month, 0).getDate()
        }
        let fractionOfTheMonth =
            (today.getDate() - 1) / daysInMonth(today.getFullYear(), today.getMonth() + 1)

        let dCogsYTD = salesDataPayload.CalendarYearCOGSYTD
        if (!dCogsYTD) dCogsYTD = 0

        let dCogsPYTD = salesDataPayload.CalendarYearCOGSPYTD
        if (!dCogsPYTD) dCogsPYTD = 0

        let dDelta = dCogsYTD - dCogsPYTD
        let dDeltaPercent = 0
        if (dCogsPYTD !== 0) dDeltaPercent = dCogsYTD / dCogsPYTD

        let salesLabelData = salesProcessSalesLabels.find((l) => l.Label === 'COGS')

        setSalesProcessSalesDataPayload((curArray) => [
            ...curArray.filter((c) => c.Label !== salesLabelData?.Label),
            {
                Label: salesLabelData ? salesLabelData.Label : '',
                SecondaryLabel: salesLabelData ? salesLabelData.SecondaryLabel : '',
                SortOrder: salesLabelData ? salesLabelData.SortOrder : 0,
                YTD: dCogsYTD,
                PYTD: dCogsPYTD,
                Delta: dDelta,
                DeltaPercent: dDeltaPercent,
                Plan: undefined,
                QuotaYoY: undefined,
                Gap: undefined,
                GapPercent: undefined,
            },
        ])
    }

    const hideLoadingVeilIfAllCalculationsAreComplete = () => {
        if (
            Object.keys(props.selectedInputs).length === 0 ||
            (opportunityDataPayload && bookingsDataPayload && planDataPayload && salesDataPayload)
        )
            props.onLoadingChange(false)
    }

    const renderSalesOpportunityRows = () => {
        let oppRows = []
        let sortedOpportunityDataPayload = opportunityDataPayload.sort(
            (a, b) => a.SortOrder - b.SortOrder
        )
        for (let i = 0; i < sortedOpportunityDataPayload.length; i++) {
            oppRows.push(
                <React.Fragment key={`salesOpportunity${i}`}>
                    <div className={styles.dataLabel}>{sortedOpportunityDataPayload[i].Stage}</div>
                    <div className={styles.dataPercent}>
                        {(sortedOpportunityDataPayload[i].Weight
                            ? sortedOpportunityDataPayload[i].Weight
                            : 0
                        ).toLocaleString('en-US', {
                            style: 'percent',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })}
                    </div>
                    <div className={styles.dataCurrency}>
                        {(sortedOpportunityDataPayload[i].YTD
                            ? sortedOpportunityDataPayload[i].YTD
                            : 0
                        ).toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })}
                    </div>
                    <div className={styles.dataCurrency}>
                        {(sortedOpportunityDataPayload[i].PYTD
                            ? sortedOpportunityDataPayload[i].PYTD
                            : 0
                        ).toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })}
                    </div>
                    <div className={styles.dataCurrency}>
                        {(sortedOpportunityDataPayload[i].Delta
                            ? sortedOpportunityDataPayload[i].Delta
                            : 0
                        ).toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })}
                    </div>
                    <div className={styles.dataPercent}>
                        {(sortedOpportunityDataPayload[i].DeltaPercent
                            ? sortedOpportunityDataPayload[i].DeltaPercent
                            : 0
                        ).toLocaleString('en-US', {
                            style: 'percent',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })}
                    </div>
                    <div className={styles.dataPercent}>
                        {(sortedOpportunityDataPayload[i].PercentOfStages
                            ? sortedOpportunityDataPayload[i].PercentOfStages
                            : 0
                        ).toLocaleString('en-US', {
                            style: 'percent',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })}
                    </div>
                </React.Fragment>
            )
        }
        return oppRows
    }

    const handleChartRefresh = (chartOptions: any, themeOptions: any, chartInstance: any) => {
        if (refreshChart) {
            chartInstance.setOptions(chartOptions, themeOptions)
        }
    }

    const renderSalesProcessSalesRows = () => {
        let spsRows = []
        let sortedSalesProcessSalesDataPayload = getSalesProcessSalesDataPayload().sort(
            (a, b) => a.SortOrder - b.SortOrder
        )

        for (let i = 0; i < sortedSalesProcessSalesDataPayload.length; i++) {
            spsRows.push(
                <React.Fragment key={`salesProcessSales${i}`}>
                    <div className={styles.dataLabel}>
                        <span className={styles.dataLabelPrimary}>
                            {sortedSalesProcessSalesDataPayload[i].Label ? (
                                sortedSalesProcessSalesDataPayload[i].Label == 'Bookings' ? (
                                    <HoverIcon
                                        growLeft={false}
                                        growDown={false}
                                        growUp={false}
                                        renderIcon={() => <span>Bookings</span>}
                                    >
                                        {(closeBubble: () => void) => (
                                            <div
                                                className={styles.identifierBubble}
                                                style={{ width: 270 }}
                                            >
                                                <div>
                                                    Bookings include product, labor, and commissions
                                                    consistent with the presentation of sales and
                                                    backlog.
                                                </div>
                                            </div>
                                        )}
                                    </HoverIcon>
                                ) : (
                                    sortedSalesProcessSalesDataPayload[i].Label
                                )
                            ) : (
                                ''
                            )}
                        </span>
                        {sortedSalesProcessSalesDataPayload[i].SecondaryLabel ? (
                            <span className={styles.dataLabelSecondary}>
                                ({sortedSalesProcessSalesDataPayload[i].SecondaryLabel})
                            </span>
                        ) : (
                            ''
                        )}
                    </div>
                    <div className={styles.dataCurrency}>
                        {sortedSalesProcessSalesDataPayload[i].Label === 'Backlog' &&
                        !backlogDataPayload ? (
                            <div
                                className={styles.app}
                                style={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    left: '-59px',
                                    width: '118px',
                                    height: '40px',
                                    zIndex: 1,
                                }}
                            >
                                <div className={'centered'} style={{ height: '100%' }}>
                                    <div className={'k-i-loading'} style={{ fontSize: 30 }} />
                                </div>
                            </div>
                        ) : sortedSalesProcessSalesDataPayload[i].Label ? (
                            sortedSalesProcessSalesDataPayload[i].YTD?.toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                            })
                        ) : (
                            ''
                        )}
                    </div>
                    <div className={styles.dataCurrency}>
                        {sortedSalesProcessSalesDataPayload[i].Label
                            ? sortedSalesProcessSalesDataPayload[i].PYTD?.toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                              })
                            : ''}
                    </div>
                    <div className={styles.dataCurrency}>
                        {sortedSalesProcessSalesDataPayload[i].Label
                            ? sortedSalesProcessSalesDataPayload[i].Delta?.toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                              })
                            : ''}
                    </div>
                    <div className={styles.dataPercent}>
                        {sortedSalesProcessSalesDataPayload[i].Label
                            ? sortedSalesProcessSalesDataPayload[i].DeltaPercent?.toLocaleString(
                                  'en-US',
                                  {
                                      style: 'percent',
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                  }
                              )
                            : ''}
                    </div>
                    <div className={styles.dataCurrency}>
                        {sortedSalesProcessSalesDataPayload[i].Label &&
                        sortedSalesProcessSalesDataPayload[i].Plan
                            ? sortedSalesProcessSalesDataPayload[i].Plan?.toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                              })
                            : ''}
                    </div>
                    <div className={styles.dataCurrency}>
                        {sortedSalesProcessSalesDataPayload[i].Label &&
                            (sortedSalesProcessSalesDataPayload[i].Label === 'Bookings' ||
                                sortedSalesProcessSalesDataPayload[i].Label === 'Sales') && (
                                <MoneyInput
                                    id='quotaYoY'
                                    variant='outlined'
                                    tabIndex={1}
                                    onBlur={(
                                        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
                                    ) => {
                                        e.target.value = e.target.value.replace(/[^0-9./-]/g, '')
                                        let numVal: number =
                                            e.target.value === '' ? 0 : parseFloat(e.target.value)

                                        sortedSalesProcessSalesDataPayload[i].Label ===
                                            'Bookings' && setBookingsQuotaYoY(numVal)
                                        sortedSalesProcessSalesDataPayload[i].Label === 'Sales' &&
                                            setSalesQuotaYoY(numVal)
                                        if (e.target.value !== '')
                                            e.target.value = numVal.toLocaleString('en-US', {
                                                style: 'currency',
                                                currency: 'USD',
                                            })
                                    }}
                                    onFocus={(
                                        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
                                    ) => e.target.select()}
                                />
                            )}
                    </div>
                    <div className={styles.dataCurrency}>
                        {sortedSalesProcessSalesDataPayload[i].Label
                            ? sortedSalesProcessSalesDataPayload[i].Gap?.toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                              })
                            : ''}
                    </div>
                    <div className={styles.dataPercent}>
                        {sortedSalesProcessSalesDataPayload[i].Label
                            ? sortedSalesProcessSalesDataPayload[i].GapPercent?.toLocaleString(
                                  'en-US',
                                  {
                                      style: 'percent',
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                  }
                              )
                            : ''}
                    </div>
                    <div></div>
                </React.Fragment>
            )
        }
        return spsRows
    }

    function getSalesProcessSalesDataPayload() {
        // if (salesProcessSalesDataPayload.length > 0) return salesProcessSalesDataPayload

        return salesProcessSalesDataPayload.concat(
            new Array(4 - salesProcessSalesDataPayload.length).fill({
                Label: '',
                SecondaryLabel: '',
                SortOrder: 1000,
                YTD: 0,
                PYTD: 0,
                Delta: 0,
                DeltaPercent: 0,
                Plan: 0,
                QuotaYoY: 0,
                Gap: 0,
                GapPercent: 0,
            })
        )
    }

    return (
        <div className={styles.salesProcess} id='sales-process'>
            <div className={`${styles.dataContainer} ${styles.opportunities}`}>
                {/* Row 1 */}
                <div className={styles.titleRow}>
                    <div className={styles.alignLeft}>Opportunities</div>
                    <div>Weight</div>
                    <div style={{ flexWrap: 'wrap' }}>
                        <span className={styles.dataLabelPrimary}>YTD</span>
                        <span className={styles.dataLabelSecondary}>(w/o weight %)</span>
                    </div>
                    <div style={{ flexWrap: 'wrap' }}>
                        <span className={styles.dataLabelPrimary}>PYTD</span>
                        <span className={styles.dataLabelSecondary}>(w/o weight %)</span>
                    </div>
                    <div>Delta $</div>
                    <div>Delta %</div>
                    <div>% of Stages</div>
                    <div></div>
                    <div className={styles.alignLeft}>Leads</div>
                    <div>Qty</div>
                </div>
                <div className={styles.dataRows}>
                    <div className={styles.opportunityData}>{renderSalesOpportunityRows()}</div>
                    <div className={styles.chartContainer}>
                        <Chart
                            style={{ height: '100%', width: '100%' }}
                            transitions={!props.loading}
                            onRefresh={handleChartRefresh}
                        >
                            <ChartTitle
                                text='Sales Opportunity Funnel'
                                align='left'
                                font='bold 12px Roboto'
                            />
                            <ChartSeries>
                                <ChartSeriesItem
                                    type='funnel'
                                    data={opportunityDataPayload}
                                    field='YTD'
                                    categoryField='Stage'
                                    padding={10}
                                    dynamicHeight={false}
                                    dynamicSlope={false}
                                >
                                    <ChartSeriesLabels
                                        color='white'
                                        background='none'
                                        align='center'
                                        content={(e: { dataItem: SalesOpportunityData }) => {
                                            return `${e.dataItem.YTD.toLocaleString('en-US', {
                                                style: 'currency',
                                                currency: 'USD',
                                                minimumFractionDigits: 0,
                                                maximumFractionDigits: 0,
                                            })}`
                                        }}
                                    />
                                </ChartSeriesItem>
                            </ChartSeries>
                        </Chart>
                    </div>
                    <div className={styles.leadData}>
                        <div>Open</div>
                        <div className={styles.dataValue}>
                            <TextField
                                id='openLeads'
                                variant='outlined'
                                tabIndex={11}
                                onBlur={(e) => {
                                    e.target.value = e.target.value.replace(/[^0-9./-]/g, '')
                                    let numVal: number =
                                        e.target.value === '' ? 0 : parseInt(e.target.value)
                                    e.target.value = numVal === 0 ? '' : numVal.toString()

                                    setOpenLeads(numVal)
                                    setLeadsTotal(
                                        numVal +
                                            contactingLeads +
                                            waitingForInfoLeads +
                                            quotingLeads +
                                            waitingForPOLeads
                                    )

                                    setRefreshChart(false)
                                }}
                                onFocus={(e) => e.target.select()}
                            />
                        </div>
                        <div>Contacting</div>
                        <div className={styles.dataValue}>
                            <TextField
                                id='contactingLeads'
                                variant='outlined'
                                tabIndex={12}
                                onBlur={(e) => {
                                    e.target.value = e.target.value.replace(/[^0-9./-]/g, '')
                                    let numVal: number =
                                        e.target.value === '' ? 0 : parseInt(e.target.value)
                                    e.target.value = numVal === 0 ? '' : numVal.toString()

                                    setContactingLeads(numVal)
                                    setLeadsTotal(
                                        openLeads +
                                            numVal +
                                            waitingForInfoLeads +
                                            quotingLeads +
                                            waitingForPOLeads
                                    )

                                    setRefreshChart(false)
                                }}
                                onFocus={(e) => e.target.select()}
                            />
                        </div>
                        <div>Waiting for Info</div>
                        <div className={styles.dataValue}>
                            <TextField
                                id='waitingForInfoLeads'
                                variant='outlined'
                                tabIndex={13}
                                onBlur={(e) => {
                                    e.target.value = e.target.value.replace(/[^0-9./-]/g, '')
                                    let numVal: number =
                                        e.target.value === '' ? 0 : parseInt(e.target.value)
                                    e.target.value = numVal === 0 ? '' : numVal.toString()

                                    setWaitingForInfoLeads(numVal)
                                    setLeadsTotal(
                                        openLeads +
                                            contactingLeads +
                                            numVal +
                                            quotingLeads +
                                            waitingForPOLeads
                                    )

                                    setRefreshChart(false)
                                }}
                                onFocus={(e) => e.target.select()}
                            />
                        </div>
                        <div>Quoting</div>
                        <div className={styles.dataValue}>
                            <TextField
                                id='quotingLeads'
                                variant='outlined'
                                tabIndex={14}
                                onBlur={(e) => {
                                    e.target.value = e.target.value.replace(/[^0-9./-]/g, '')
                                    let numVal: number =
                                        e.target.value === '' ? 0 : parseInt(e.target.value)
                                    e.target.value = numVal === 0 ? '' : numVal.toString()

                                    setQuotingLeads(numVal)
                                    setLeadsTotal(
                                        openLeads +
                                            contactingLeads +
                                            waitingForInfoLeads +
                                            numVal +
                                            waitingForPOLeads
                                    )

                                    setRefreshChart(false)
                                }}
                                onFocus={(e) => e.target.select()}
                            />
                        </div>
                        <div>Waiting for PO</div>
                        <div className={styles.dataValue}>
                            <TextField
                                id='waitingForPOLeads'
                                variant='outlined'
                                tabIndex={15}
                                onBlur={(e) => {
                                    e.target.value = e.target.value.replace(/[^0-9./-]/g, '')
                                    let numVal: number =
                                        e.target.value === '' ? 0 : parseInt(e.target.value)
                                    e.target.value = numVal === 0 ? '' : numVal.toString()

                                    setWaitingForPOLeads(numVal)
                                    setLeadsTotal(
                                        openLeads +
                                            contactingLeads +
                                            waitingForInfoLeads +
                                            quotingLeads +
                                            numVal
                                    )

                                    setRefreshChart(false)
                                }}
                                onFocus={(e) => e.target.select()}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.totalRow}>
                    <div className={styles.dataLabel}>Total</div>
                    <div></div>
                    <div className={`${styles.bordered} ${styles.dataCurrency}`}>
                        {ytdTotal.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })}
                    </div>
                    <div className={`${styles.bordered} ${styles.dataCurrency}`}>
                        {pytdTotal.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })}
                    </div>
                    <div className={`${styles.bordered} ${styles.dataCurrency}`}>
                        {deltaTotal.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })}
                    </div>
                    <div className={`${styles.bordered} ${styles.dataPercent}`}>
                        {deltaPercentTotal.toLocaleString('en-US', {
                            style: 'percent',
                            maximumFractionDigits: 0,
                        })}
                    </div>
                    <div className={`${styles.bordered} ${styles.dataPercent}`}>
                        {percentOfStagesTotal.toLocaleString('en-US', {
                            style: 'percent',
                            maximumFractionDigits: 0,
                        })}
                    </div>
                    <div></div>
                    <div className={styles.dataLabel}>Total</div>
                    <div className={styles.dataValue}>{leadsTotal}</div>
                </div>
            </div>
            <div className={`${styles.dataContainer} ${styles.salesProcessSales}`}>
                {/* Row 1 */}
                <div className={styles.titleRow}>
                    <div className={styles.alignLeft}>Sales</div>
                    <div>YTD</div>
                    <div>PYTD</div>
                    <div>Delta $</div>
                    <div>Delta %</div>
                    <div>AWC's Plan</div>
                    <div>Quota YoY</div>
                    <div>Gap $</div>
                    <div>Gap %</div>
                </div>
                <div className={`${styles.dataRows} ${styles.salesProcessSales}`}>
                    {renderSalesProcessSalesRows()}
                </div>
            </div>
        </div>
    )
}

export default SalesProcessData
